import {
  Box,
  Button,
  HStack,
  Input,
  Link,
  Spinner,
  Td,
  Text,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { AdminBadge } from '../../../components/StatusBadge/StatusBadge'
import type { ProbeStatus } from '../ListTablesTestPage/ListTablesTestPage.parts'
import { TableProbeRunner } from './tableProbeRunner.parts'

export type ProbeStatusBadgeProps = {
  status: ProbeStatus
  'data-testid'?: string
}

const statusColor = (status: ProbeStatus) => {
  switch (status) {
    case 'pass':
      return 'green'
    case 'fail':
      return 'orange'
    case 'error':
      return 'red'
    default:
      return 'blue'
  }
}

const statusLabel = (status: ProbeStatus) => {
  switch (status) {
    case 'pass':
      return 'Pass'
    case 'fail':
      return 'Fail'
    case 'error':
      return 'Error'
    default:
      return 'Running'
  }
}

export const ProbeStatusBadge = ({
  status,
  'data-testid': dataTestId,
}: ProbeStatusBadgeProps) => (
  <HStack gap="6px" alignItems="center">
    {status === 'running' && <Spinner size="xs" color="presets.blue.50" />}
    <AdminBadge colorScheme={statusColor(status)} data-testid={dataTestId}>
      {statusLabel(status)}
    </AdminBadge>
  </HStack>
)

export type ProbePassDetailsRowProps = {
  columnCount: number
  isExpanded: boolean
  passRecordPath?: string
  recordsChecked?: number
  status: ProbeStatus
  errorMessage?: string
}

export const ProbePassDetailsRow = ({
  columnCount,
  isExpanded,
  passRecordPath,
  recordsChecked,
  status,
  errorMessage,
}: ProbePassDetailsRowProps) => {
  const [copied, setCopied] = useState(false)

  const copyLink = useCallback(async () => {
    if (!passRecordPath) {
      return
    }

    await navigator.clipboard.writeText(
      TableProbeRunner.buildAbsoluteRecordUrl(passRecordPath),
    )
    setCopied(true)
  }, [passRecordPath])

  if (!isExpanded || status === 'running') {
    return null
  }

  const absoluteUrl = passRecordPath
    ? TableProbeRunner.buildAbsoluteRecordUrl(passRecordPath)
    : undefined

  return (
    <Tr>
      <Td colSpan={columnCount} p="0" borderBottomWidth="1px">
        <Box p="12px" bg="presets.neutral.5">
          <VStack alignItems="stretch" gap="8px">
            {status === 'pass' && passRecordPath && absoluteUrl && (
              <>
                <Text fontSize="xs" fontWeight="600">
                  Passing record link
                </Text>
                <HStack gap="8px" alignItems="center">
                  <Input
                    size="sm"
                    value={absoluteUrl}
                    readOnly
                    flex="1"
                    minW={0}
                    data-testid="probe-pass-record-url"
                  />
                  <Button size="sm" onClick={() => void copyLink()}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                  <Button as={RouterLink} to={passRecordPath} size="sm">
                    Open
                  </Button>
                </HStack>
              </>
            )}

            {recordsChecked !== undefined && (
              <Text fontSize="xs" color="presets.neutral.60">
                Checked {recordsChecked} record
                {recordsChecked === 1 ? '' : 's'} in this environment
              </Text>
            )}

            {status === 'fail' && errorMessage && (
              <Text fontSize="xs" color="presets.pink.70">
                {errorMessage}
              </Text>
            )}

            {status === 'error' && errorMessage && (
              <Text fontSize="xs" color="presets.pink.70">
                {errorMessage}
              </Text>
            )}

            {status === 'pass' && passRecordPath && (
              <Link
                as={RouterLink}
                to={passRecordPath}
                color="presets.turquoise.100"
                fontSize="xs"
              >
                {passRecordPath}
              </Link>
            )}
          </VStack>
        </Box>
      </Td>
    </Tr>
  )
}
